import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Domain literals (mirror the backend's CHECK constraints)
// ────────────────────────────────────────────────────────────────────────────

export const NEWS_ARTICLE_KINDS = ['article', 'video'] as const;
export const NEWS_ARTICLE_STATUSES = ['draft', 'published', 'archived'] as const;

export type NewsArticleKind = (typeof NEWS_ARTICLE_KINDS)[number];
export type NewsArticleStatus = (typeof NEWS_ARTICLE_STATUSES)[number];

export const NEWS_ARTICLE_STATUS_LABELS: Record<NewsArticleStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const YOUTUBE_VIDEO_ID_REGEX = /^[A-Za-z0-9_-]{11}$/;

// ────────────────────────────────────────────────────────────────────────────
// Server-shaped row (validated when consumed)
// ────────────────────────────────────────────────────────────────────────────

export const newsArticleSchema = z.object({
  id: z.string(),
  kind: z.enum(NEWS_ARTICLE_KINDS),
  youtubeVideoId: z.string().nullable(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  imageStorageKey: z.string().nullable(),
  thumbnailStorageKey: z.string().nullable(),
  status: z.enum(NEWS_ARTICLE_STATUSES),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
export type NewsArticle = z.infer<typeof newsArticleSchema>;

const newsArticleTagSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});
const newsArticleTranslationSchema = z.object({
  id: z.string(),
  localeId: z.string(),
  localeCode: z.string(),
  title: z.string(),
  summary: z.string(),
});
export const newsArticleDetailSchema = newsArticleSchema.extend({
  tags: z.array(newsArticleTagSchema),
  translations: z.array(newsArticleTranslationSchema),
});
export type NewsArticleDetail = z.infer<typeof newsArticleDetailSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Form schema
// ────────────────────────────────────────────────────────────────────────────

export const newsArticleFormTranslationSchema = z.object({
  localeCode: z.string().min(1),
  localeName: z.string(), // display-only; stripped before submit
  title: z.string().max(500),
  summary: z.string().max(2000),
});
export type NewsArticleFormTranslation = z.infer<
  typeof newsArticleFormTranslationSchema
>;

const baseFields = {
  kind: z.enum(NEWS_ARTICLE_KINDS),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(200, 'Slug must be at most 200 characters')
    .regex(SLUG_REGEX, 'Lowercase alphanumerics joined by single hyphens'),
  title: z.string().min(1, 'Title is required').max(500),
  summary: z.string().min(1, 'Summary is required').max(2000),
  status: z.enum(NEWS_ARTICLE_STATUSES),
  imageStorageKey: z.string().max(500).nullable(),
  thumbnailStorageKey: z.string().max(500).nullable(),
  youtubeVideoId: z
    .string()
    .max(11)
    .regex(YOUTUBE_VIDEO_ID_REGEX, '11-char YouTube ID, not a URL')
    .nullable(),
  translations: z.array(newsArticleFormTranslationSchema),
  // Tag ids as strings (BigInt-safe over the wire); capped to mirror the API.
  tagIds: z.array(z.string()).max(20),
};

export const newsArticleFormSchema = z
  .object(baseFields)
  .superRefine((v, ctx) => {
    if (v.kind === 'video' && !v.youtubeVideoId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'YouTube video ID is required for video articles',
        path: ['youtubeVideoId'],
      });
    }
    if (v.kind === 'article' && v.youtubeVideoId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'YouTube video ID must be empty for text articles',
        path: ['youtubeVideoId'],
      });
    }
    if (v.kind === 'article' && !v.imageStorageKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Image is required for articles',
        path: ['imageStorageKey'],
      });
    }
    if (v.kind === 'video' && v.imageStorageKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Image must be empty for video articles',
        path: ['imageStorageKey'],
      });
    }
    // Block half-filled translation rows; fully-empty rows are filtered before submit.
    v.translations.forEach((t, i) => {
      const hasTitle = t.title.trim().length > 0;
      const hasSummary = t.summary.trim().length > 0;
      if (hasTitle && !hasSummary) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Add a ${t.localeName} summary or clear the title`,
          path: ['translations', i, 'summary'],
        });
      }
      if (hasSummary && !hasTitle) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Add a ${t.localeName} title or clear the summary`,
          path: ['translations', i, 'title'],
        });
      }
    });
  });
export type NewsArticleFormValues = z.infer<typeof newsArticleFormSchema>;

// ────────────────────────────────────────────────────────────────────────────
// List filters
// ────────────────────────────────────────────────────────────────────────────

// Mirror of NewsArticleSortKeyValues in the API.
export const NEWS_ARTICLE_SORT_KEYS = [
  'title',
  'status',
  'published',
  'updated',
] as const;
export type NewsArticleSortKey = (typeof NEWS_ARTICLE_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface ListNewsArticlesParams {
  page: number;
  pageSize: number;
  searchText?: string;
  status?: NewsArticleStatus;
  kind?: NewsArticleKind;
  sortBy?: NewsArticleSortKey;
  sortDir?: SortDirection;
}
